// RecordDetails.js
import React, { useState, useEffect } from 'react';
import { Form, Card, Input, Button, Row, Col, Typography, Avatar, Select, Tabs, Checkbox, message, Space, DatePicker, Collapse, Affix, Tooltip, Spin } from 'antd';
import { EditOutlined, UnorderedListOutlined, InfoCircleOutlined, PhoneOutlined } from '@ant-design/icons';
import { BASE_URL, currencyMap, DateFormat, DateTimeFormat } from '../Components/Constant';
import ApiService from '../Components/apiService'; // Import ApiService class
import dayjs from 'dayjs';
import FieldRendererEdit from './RecordFieldEditor';
import DisplayField from './RecordFieldDisplay';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { colors, convertDateToUserTimezone, getUniqueColor, useHoverVisibility } from '../Components/Utility';
import LookupDisplayCard from './LookupDisplayCard';
import eventBus from '../Components/eventBus'; // Import the event bus
import { useSelector } from 'react-redux';
import isEqual from "lodash/isEqual";
import { set } from 'lodash';
import { generateBody, generateUTCDate, generateUTCMidnightDate } from '../Components/Utility'; // Import the utility function
import { Vault } from 'lucide-react';
import Cookies from 'js-cookie';
import Feed from '../CommentFeed/Feed';
import { use } from 'react';


dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);


const { Title } = Typography;

const RecordDetails = ({
    objectName,
    id,
    recordDetails,
    fetchRecordData,
    Recordfields,
    layout,
    isGrid,
    fetchCurrency,
    getConvertedValue
}) => {

    const {
        isHovered: isCreatedByHovered,
        isVisible: isCreatedByVisible,
        handleMouseEnter: handleCreatedByMouseEnter,
        handleMouseLeave: handleCreatedByMouseLeave,
        handleContentMouseEnter: handleCreatedByContentMouseEnter,
        handleContentMouseLeave: handleCreatedByContentMouseLeave,
    } = useHoverVisibility();

    const {
        isHovered: isUpdatedByHovered,
        isVisible: isUpdatedByVisible,
        handleMouseEnter: handleUpdatedByMouseEnter,
        handleMouseLeave: handleUpdatedByMouseLeave,
        handleContentMouseEnter: handleUpdatedByContentMouseEnter,
        handleContentMouseLeave: handleUpdatedByContentMouseLeave,
    } = useHoverVisibility();

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);  // Initial state is true to show spinner initially
    const [lookupOptionforparent, setLookupOptionsForParent] = useState([]);
    const [isEditable, setIsEditable] = useState(false);
    const [record, setRecord] = useState(null);
    const [fields, setFields] = useState([]);
    const [selectedDate, setSelectedDate] = useState({});
    const [initialValues, setInitialValues] = useState({});
    const loginUserCurrency = Cookies.get('userCurrency');
    const [recordTypes, setRecordTypes] = useState([]);
    const [selectedRecordTypeId, setSelectedRecordTypeId] = useState(null);
    const [richTextValues, setRichTextValues] = useState({}); // To store rich text field values    

    const [createdByField, setCreatedByField] = useState([]);
    const [LastModifiedByField, setLastModifiedByField] = useState([]);
    const security = useSelector(state => state.data.items[0]);
    const [mapOfDateDateTimeFieldWithValue, setMapOfDateDateTimeFieldWithValue] = useState({});
    const [activeCurrencies, setActiveCurrencies] = useState([]);
    const [recordLocked, setRecordLocked] = useState(false);

    useEffect(() => {
        fetchMetadata(); // Fetch metadata only once
    }, [id, objectName, Recordfields]);

    useEffect(() => {
        fetchRecords();  // Fetch records on mount
    }, [fields, recordDetails]);

    useEffect(() => {
        fetchCurrency();  // Fetch mt_currencies
        fetchRecordTypes();
    }, []);

    const fetchMetadata = async () => {
        try {
            setLoading(true);
            const filteredFields = Recordfields?.filter(
                field => !['recordCount', 'CreatedBy', 'LastModifiedBy'].includes(field?.name)
            );

            const createdBy = Recordfields?.filter(field => field?.name === 'CreatedBy');
            const lastModifiedBy = Recordfields?.filter(field => field?.name === 'LastModifiedBy');

            setCreatedByField(createdBy);
            setLastModifiedByField(lastModifiedBy);
            setFields(filteredFields);

        } catch (err) {
            console.error("Error fetching metadata:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        eventBus.on('EditModeOn', handleEditClick);

    }, []);

    useEffect(() => {
        const handleApprovalCreated = (message) => {
            if (message === 'InApproval') {
                setRecordLocked(true);
            }
        };

        eventBus.on('ApprovalCreated', handleApprovalCreated);
        return () => eventBus.off('ApprovalCreated', handleApprovalCreated);
    }, []);

    const handleEditClick = () => {
        setIsEditable(true);
    };

    const handleCancelClick = () => {
        form.setFieldsValue(initialValues);
        setIsEditable(false);
    };

    const fetchRecords = async () => {
        try {
            setLoading(true);
            const recordData = recordDetails;
            let updatedData;
            const updatedMap = {}; // Temporary object to store date values
            const updatedRichTextValues={}; // Temporary object to store rich text values

            if (recordData && fields?.length > 0) {
                updatedData = { ...recordData };
                fields?.forEach(field => {

                    if (field?.type === 'percentage' && updatedData[field?.name]) {
                        updatedData[field?.name] = updatedData[field?.name];
                    }
                    if (field?.type === 'date' && updatedData[field?.name]) {
                        const converteddate = convertDateToUserTimezone(updatedData[field.name], DateFormat);
                        updatedData[field?.name] = converteddate;
                        updatedMap[field?.name] = converteddate;
                    }
                    if (field?.type === 'datetime' && updatedData[field?.name]) {
                        const converteddate = convertDateToUserTimezone(updatedData[field.name], DateTimeFormat);
                        updatedData[field?.name] = converteddate;
                        updatedMap[field?.name] = converteddate
                    }
                    if(field.type==='richtext' && updatedData[field?.name]){
                        updatedData[field?.name]=updatedData[field?.name];
                        updatedRichTextValues[field?.name]=updatedData[field?.name];
                    }
                    if (field?.type === 'lookup' && updatedData[field?.name]) {
                        updatedData[field?.name] = updatedData[field?.name] || '';
                    }
                    if ((field?.type === 'formula' || field?.is_formula) && updatedData[field?.name]) {
                        const value = updatedData[field?.name];
                        if (value && typeof value === 'object' && !Array.isArray(value)) {
                            const addressKeys = ['street', 'city', 'state', 'country', 'postal_code', 'pincode'];
                            const addressParts = addressKeys.map(k => value[k]).filter(Boolean);
                            if (addressParts.length > 0) {
                                updatedData[field?.name] = addressParts.join(', ');
                            } else {
                                updatedData[field?.name] = JSON.stringify(value);
                            }
                        }
                    }
                });
                setMapOfDateDateTimeFieldWithValue(updatedMap);
                setRichTextValues(updatedRichTextValues);

            }
            setRecord(updatedData);
            setInitialValues(updatedData);
            form.setFieldsValue(updatedData);



        } catch (err) {
            console.error("Error fetching record:", err);
        } finally {
            setLoading(false);
        }
    };

    const onFinish = async (values) => {
        console.log('Form values on finish:', values);
        try {
            setLoading(true);
            const bodyData = { ...values };
            let customerror = '';
            for (const field of fields) {
                if (bodyData.hasOwnProperty(field?.name)) {
                    if (field?.type === 'lookup' || field?.name === 'CreatedBy' || field?.name === 'LastModifiedBy') {
                        let lookupFieldName = field?.name;

                        if (bodyData[field?.name]?._id) {
                            bodyData[lookupFieldName] = bodyData[field?.name]._id;
                        } else {
                            bodyData[lookupFieldName] = bodyData[field?.name] || null;
                        }
                    }

                    else if (field?.type === 'percentage' && bodyData[field?.name]) {
                        bodyData[field?.name] = bodyData[field?.name] / 100;
                    }

                    else if ((field.type === 'richtext' || field.type === 'textarea') && field.required && (values[field.name] != '' || values[field.name] != null || values[field.name] != undefined) && values[field.name].replace(/<[^>]*>/g, '').trim() === '') {
                        message.error(`${field.label} is required`);
                        customerror = "error";
                    }

                    else if (field?.type === 'richtext' ) {
                        bodyData[field?.name] = richTextValues[field?.name];
                    }

                    else if (field?.type === 'address' && bodyData[field?.name]) {
                        bodyData[field?.name] = {
                            street: bodyData[field?.name]['street'] || '',
                            city: bodyData[field?.name]['city'] || '',
                            state: bodyData[field?.name]['state'] || '',
                            postal_code: bodyData[field?.name]['postal_code'] || '',
                            country: bodyData[field?.name]['country'] || '',
                        };
                    }

                    else if (field?.type === 'date') {
                        if (mapOfDateDateTimeFieldWithValue[field?.name]) {
                            const utcDate = generateUTCMidnightDate(mapOfDateDateTimeFieldWithValue[field?.name]);
                            bodyData[field?.name] = utcDate; // <-- assign converted value
                        } else {
                            console.log(`Date field ${field?.name} missing in map`);
                        }
                    }

                    else if (field?.type === 'datetime') {
                        if (mapOfDateDateTimeFieldWithValue[field?.name]) {
                            const utcDate = generateUTCDate(mapOfDateDateTimeFieldWithValue[field?.name]);
                            bodyData[field?.name] = utcDate;
                        } else {
                            console.log(`DateTime field ${field?.name} missing in map`);
                        }
                    }
                }
            }


            if (customerror === "error") {
                return;
            }
            const sanitizedData = Object.keys(bodyData).reduce((acc, key) => {
                acc[key] = bodyData[key] === undefined ? "" : bodyData[key];
                return acc;
            }, {});

            sanitizedData['_id'] = record?._id;

            const changedValues = {};

            Object.keys(sanitizedData).forEach((key) => {
                const newValue = sanitizedData[key];
                const oldValue = initialValues[key];

                const isNewKey = !(key in initialValues);
                const isEmpty = newValue === "" || newValue === null || newValue === undefined;

                if (!isEqual(newValue, oldValue)) {
                    if (!isNewKey || (isNewKey && !isEmpty)) {
                        changedValues[key] = newValue;
                    }
                }
            });


            if (Object.keys(changedValues).length === 0) {
                setIsEditable(false);
                return;
            }

            changedValues['_id'] = record?._id;

            const body = {
                object_name: objectName,
                data: changedValues,
            };

            const apiService = new ApiService(`${BASE_URL}/insert_or_update_records`, {
                'Content-Type': 'application/json',
            }, 'POST', body);
            await apiService.makeCall();
            message.success('Record saved successfully');
            eventBus.emit('RecordSaved');

            fetchRecordData();
            fetchRecords();

        } catch (error) {
            const errorMessage = error && typeof error === 'object'
                ? Object.entries(error).map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`).join(' | ')
                : 'Failed to save record due to an unknown error';
            message.error(errorMessage);
        } finally {
            setIsEditable(false);
            setLoading(false);

        }

    };

    const handleSearch = async (value, fieldId, name) => {

        if (value) {

            try {
                const apiService = new ApiService(`${BASE_URL}/search_lookup/${fieldId}/${value}`, {
                    'Content-Type': 'application/json',
                }, 'POST', { child_context: record });
                const response = await apiService.makeCall();

                setLookupOptionsForParent(prevOptions => ({
                    ...prevOptions,
                    [name]: response
                }));
            } catch (error) {
                setLookupOptionsForParent(prevOptions => ({
                    ...prevOptions,
                    [name]: []
                }))
            } finally {
            }
        } else {
            setLookupOptionsForParent(prevOptions => ({
                ...prevOptions,
                [name]: []
            }));
        }
    };

    const fetchRecordTypes = async () => {
        try {
            const api = new ApiService(`${BASE_URL}/record_types/by_object/${objectName}`);
            const response = await api.makeCall();
            setRecordTypes(response || []);
            if (response?.length === 1) {
                setSelectedRecordTypeId(response[0]._id);
            }

        } catch {
            console.log('Failed to fetch record types');
        }

    }

    const renderFieldWithEdit = (field, selectedDate, setSelectedDate) => {
        if (!field) return null;
        const picklistOverride = recordTypes.find(
            (rt) => rt._id === record?.RecordType
        )?.picklist_overrides?.[field.name];

        const finalPicklistValues = picklistOverride && Array.isArray(picklistOverride.values)
            ? picklistOverride.values.filter(v => v !== null)
            : field.picklist_values || [];




        const { name, label, type, picklist_values, isTextArea, required, help_text, _id, lookup_config, parent_object_name } = field;
        const validationRules = [];
        const isFieldSecurityEditable = security?.object_settings?.[objectName]?.field_setting?.[name]?.edit;

        if (required) {
            validationRules.push({
                required: true,
                message: `${label} is required.`,
            });
        }

        if (name === 'recordCount' || name === 'CreatedBy' || name === 'LastModifiedBy') {
            return null;
        }

        if (type === 'decimal') {
        } else if (type === 'date') {
            validationRules.push({
                type: 'Date',
                message: 'Please enter a valid date.',
            });
        } else if (type === 'currency') {
            validationRules.push({
                validator(_, value) {
                    if (value === undefined || value === null || value === '') {
                        return Promise.resolve();
                    }
                    if (isNaN(value) || Number(value) < 0) {
                        return Promise.reject('Please enter a valid currency value.');
                    }
                    return Promise.resolve();
                }
            });
        } else if (type === 'email') {
            validationRules.push(
                { type: 'email', message: 'The input is not valid E-mail!' }
            );
        }

        const isFieldEditable = !field?.is_auto_number && !field?.is_formula && field?.type !== 'rollup' && isEditable && isFieldSecurityEditable;
        console.log('isFieldEditable', isEditable);
        console.log('field?.type', field?.type);
        const handleAddressChange = (parentField, childField, value) => {

            const currentAddress = form.getFieldValue(parentField);
            const newAddress = {
                ...currentAddress,
                [childField]: value,
            };
        }


        return (
            <div
                className="editable-field-container"
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    borderBottom: !isEditable ? '1px solid #ddd' : 'none',
                    paddingBottom: 0,
                    marginBottom: 4,
                    position: 'relative',
                    minHeight: 20, // Ensures no overlap

                }}
            >
                {isFieldEditable ? (
                    <FieldRendererEdit
                        isFieldEditable={isFieldEditable}
                        type={type}
                        name={name}
                        label={label}
                        validationRules={validationRules}
                        form={form}
                        picklist_values={picklist_values}
                        finalPicklistValues={finalPicklistValues}
                        handleSearch={handleSearch}
                        lookupOptionforparent={lookupOptionforparent}
                        lookup_config={lookup_config}
                        DateFormat={DateFormat}
                        setSelectedDate={setSelectedDate}
                        fieldId={field?._id}
                        handleAddressChange={handleAddressChange}
                        parentObjectName={parent_object_name}
                        mapOfDateDateTimeFieldWithValue={mapOfDateDateTimeFieldWithValue}
                        setMapOfDateDateTimeFieldWithValue={setMapOfDateDateTimeFieldWithValue}
                        setRichTextValues={setRichTextValues}
                        richTextValues={richTextValues}
                    />
                ) : (
                    type === 'currency' ? (
                        <span>
                            {record?.[name]
                                ? <>
                                    {record?.CurrencyIsoCode} {Number(record?.[name]).toFixed(2)}
                                    {loginUserCurrency !== record?.CurrencyIsoCode && (
                                        <span style={{ color: '#888', marginLeft: 6 }}>
                                            ({loginUserCurrency} {getConvertedValue(record?.[name], record?.CurrencyIsoCode, loginUserCurrency)})
                                        </span>
                                    )}
                                </>
                                : null}
                        </span>

                    ) : 
                      <div style={{ flex: 1, zIndex: 1 }}>
                        <DisplayField
                            type={type}
                            form={form}
                            name={name}
                            field={field}
                            record={record}
                        />
                      </div>

                )
                }

                {!isEditable && !recordLocked && (
                        <div
                            style={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            zIndex: 10, // this is crucial
                        }}
                        >

                    <Avatar
                        icon={<EditOutlined />}
                        size={22}
                        className="edit-icon"
                        style={{
                            marginLeft: 4,
                            cursor: 'pointer',
                            backgroundColor: 'transparent',
                            color: 'black',
                            zIndex: 1
                        }}
                        onClick={handleEditClick}

                    />
                        </div>

                )}

            </div>
        );
    };


    return (
        <>
            <Spin spinning={loading} size='medium'>

                <Form layout="vertical" onFinish={onFinish} style={{ flex: 1 }} id="recordDetailsForm">
                    <div style={{ paddingRight: '4px', flex: 1, overflow: 'visible' }}>
                        {loading ? (
                            <Spin size="large" style={{ marginTop: '20px' }} />
                        ) : (
                            layout && layout.length > 0 ? (
                                layout[0]?.sections?.map((section, sectionIndex) => (
                                    <div
                                        key={sectionIndex}
                                        style={{
                                            border: '1px solid #ddd',
                                            marginBottom: '6px',
                                            padding: '15px',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <Title level={5} style={{ marginTop: '2px', marginBottom: 4 }}>{section.name}</Title>
                                        <Row gutter={8} style={{ marginBottom: '0px' }}>
                                            {section?.columns?.map((column, columnIndex) => {
                                                const totalColumns = section.columns.length;
                                                const columnSpan = totalColumns === 1
                                                    ? 24
                                                    : totalColumns === 2
                                                        ? 12
                                                        : totalColumns === 3
                                                            ? 8
                                                            : 8;

                                                const columnItems = column.items || [];
                                                const maxItems = Math.max(...section.columns.map(c => c.items?.length || 0));

                                                return (
                                                    <Col
                                                        key={columnIndex}
                                                        span={columnSpan}
                                                        style={{ marginBottom: '6px' }}
                                                    >
                                                        <div>
                                                            {Array.from({ length: maxItems }).map((_, rowIndex) => {
                                                                const item = columnItems[rowIndex];
                                                                const matchedField = fields?.find(field => field?.name === item?.name);
                                                                return (
                                                                    item && (
                                                                        <Form.Item
                                                                            key={rowIndex}
                                                                            label={<span>{matchedField?.label}</span>}
                                                                            style={{ marginBottom: '8px', padding: '0px' }}
                                                                        >
                                                                            {renderFieldWithEdit(matchedField, selectedDate, setSelectedDate)}
                                                                        </Form.Item>
                                                                    )
                                                                );
                                                            })}
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    </div>
                                ))
                            ) : (
                                <Row gutter={16} style={{ marginBottom: '0px' }}>
                                    {fields?.map((field, index) => (
                                        <Col key={index} xs={24} sm={12} style={{ marginBottom: 4 }}>
                                            <Form.Item label={<span>{field?.label} {field?.help_text && <Tooltip title={field?.help_text}><InfoCircleOutlined style={{ marginLeft: 4 }} /></Tooltip>}</span>} style={{ marginBottom: 0, padding: '0px' }}>
                                                {renderFieldWithEdit(field, selectedDate, setSelectedDate)}
                                            </Form.Item>
                                        </Col>
                                    ))}
                                </Row>
                            )

                        )}
                        <div className="system-info-section" style={{
                            marginTop: '6px',
                            width: '100%',
                            padding: '8px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            backgroundColor: '#fff',
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <Title level={4} style={{ marginTop: '0px', marginBottom: 8 }}>System Information</Title>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item label="Created At" style={{ marginBottom: 0 }}>
                                        <Input
                                            style={{ border: 'none', borderBottom: '1px solid #ddd', fontWeight: '500', padding: 0 }}
                                            value={record?.created_at ? convertDateToUserTimezone(record.updated_at, DateTimeFormat) : ''}
                                        />
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item label="Updated At" style={{ marginBottom: 0 }}>
                                        <Input
                                            style={{ border: 'none', borderBottom: '1px solid #ddd', fontWeight: '500', padding: 0 }}
                                            value={record?.updated_at ? convertDateToUserTimezone(record.updated_at, DateTimeFormat) : ''}
                                        />
                                    </Form.Item>
                                </Col>
                            </Row>
                            <Row gutter={8}>
                                <Col span={12}>
                                    <Form.Item key="CreatedBy" label="Created By" style={{ marginBottom: 0, borderBottom: '1px solid #ddd', }}>

                                        <div
                                            style={{ position: 'relative', display: 'inline-block' }}
                                            onMouseEnter={handleCreatedByMouseEnter}
                                            onMouseLeave={handleCreatedByMouseLeave}
                                        >
                                            {record?.CreatedBy?.Name ? (
                                                <a
                                                    href={`/record/${createdByField[0]?.parent_object_name}/${record?.CreatedBy?._id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                                >


                                                    <Avatar
                                                        size="small"
                                                        src={record?.CreatedBy?.image ? `${BASE_URL}/ImageUpload/${record?.CreatedBy?.org_id}/${record?.CreatedBy?.image}.png` : undefined}
                                                        style={{ backgroundColor: "#87d068", marginRight: 6 }}
                                                    >
                                                        {record?.CreatedBy?.Name?.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    {record?.CreatedBy?.Name}
                                                </a>
                                            ) : (
                                                record?.CreatedBy?.Name
                                            )}

                                            {isCreatedByHovered && isCreatedByVisible && (
                                                <div
                                                    onMouseEnter={handleCreatedByContentMouseEnter}
                                                    onMouseLeave={handleCreatedByContentMouseLeave}
                                                >
                                                    <LookupDisplayCard
                                                        displayFields={createdByField[0]?.lookup_config?.display_fields}
                                                        fieldValue={record?.CreatedBy}
                                                        objectName={createdByField[0]?.parent_object_name}
                                                        placement={'right'}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Form.Item key="UpdatedBy" label="Updated By" style={{ marginBottom: 0, borderBottom: '1px solid #ddd', }}>
                                        <div
                                            style={{ position: 'relative', display: 'inline-block' }}
                                            onMouseEnter={handleUpdatedByMouseEnter}
                                            onMouseLeave={handleUpdatedByMouseLeave}
                                        >
                                            {record?.LastModifiedBy?.Name ? (
                                                <a
                                                    href={`/record/${LastModifiedByField[0]?.parent_object_name}/${record?.LastModifiedBy?._id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
                                                >


                                                    <Avatar
                                                        size="small"
                                                        src={record?.LastModifiedBy?.image ? `${BASE_URL}/ImageUpload/${record?.LastModifiedBy?.org_id}/${record?.LastModifiedBy?.image}.png` : undefined}
                                                        style={{ backgroundColor: "#87d068", marginRight: 6 }}
                                                    >
                                                        {record?.LastModifiedBy?.Name?.charAt(0).toUpperCase()}
                                                    </Avatar>
                                                    {record?.LastModifiedBy?.Name}
                                                </a>
                                            ) : (
                                                record?.CreatedBy?.Name
                                            )}

                                            {isUpdatedByHovered && isUpdatedByVisible && (
                                                <div
                                                    onMouseEnter={handleUpdatedByContentMouseEnter}
                                                    onMouseLeave={handleUpdatedByContentMouseLeave}
                                                >

                                                    <LookupDisplayCard
                                                        displayFields={LastModifiedByField[0]?.lookup_config?.display_fields}
                                                        fieldValue={record?.LastModifiedBy}
                                                        objectName={LastModifiedByField[0]?.parent_object_name}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </Form.Item>
                                </Col>

                            </Row>
                        </div>
                    </div>
                </Form>

                {isEditable && (
                    isGrid ? (
                        <div style={{
                            width: '100%',
                            padding: '8px 0',
                            background: 'rgba(240, 242, 245, 0.9)',
                            position: 'sticky',
                            bottom: 10,
                            left: 0,
                            zIndex: 1000,
                        }}>
                            <Row justify="center">
                                <Button onClick={handleCancelClick} style={{ marginRight: 6 }} size="small">
                                    Cancel
                                </Button>
                                <Button type="primary" htmlType="submit" form="recordDetailsForm" size="small">
                                    Save
                                </Button>
                            </Row>
                        </div>
                    ) : (
                        <Affix offsetBottom={0}>
                            <div style={{
                                width: '100%',
                                padding: '8px 0',
                                background: 'rgba(240, 242, 245, 0.9)',
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                zIndex: 1000,
                            }}>
                                <Row justify="center">
                                    <Button onClick={handleCancelClick} style={{ marginRight: 6 }} size="small">
                                        Cancel
                                    </Button>
                                    <Button type="primary" htmlType="submit" form="recordDetailsForm" size="small">
                                        Save
                                    </Button>
                                </Row>
                            </div>
                        </Affix>
                    )
                )}
            </Spin>
        </>
    );
};

export default RecordDetails;